// Тренажёр «Зевки» (#/train/blunders, макет 05): доска ориентирована стороной
// юзера, он ищет ход лучше сыгранного зевка. Верный (или ≤30cp от лучшего по
// движку на 3с) → зелёно; сыгранный в партии зевок → красно; иначе жёлто с
// подсказкой-стрелкой после второй неудачи. Серия ✓ и прогресс в localStorage.
// Под доской — тулбар (◀ ▶ листать попытку · 💡 лучший ход · ⇅ флип · 🤖 доиграть
// с ботом). «Дальше» архивирует свою позицию навсегда. Свои кончились → пазл Lichess.
import { MiniBoard } from '@ext/panel/board/mini-board';
import { ON_DEMAND, PriorityScheduler } from '../services/analysis';
import { analyze as loaderAnalyze } from '../services/engine-loader';
import {
  fenAfterUci,
  fetchLichessPuzzle,
  nextStreak,
  ownPuzzlesFromGames,
  sanToUci,
  streakAfterCorrect,
  verifyAttempt,
  type OwnPuzzle,
  type TrainPuzzle,
  type Verdict,
} from '../services/puzzles';
import { archivePuzzle } from '../services/blunder-archive';
import { AttemptNav, type Frame } from '../services/blunder-attempt';
import { BoardToolbar, type BoardHost } from './board-toolbar';
import { t } from '../i18n';
import { state } from '../state';
import { trackTraining } from '../services/track';
import { hideMainButton, showBackButton, haptic, notify } from '../tg';
import { PlayEngineController } from './play-engine';
import { PLAY_OPTS } from '../services/play-engine';

const STREAK_KEY = 'cc_train_streak';

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);
}

function loadStreak(): number {
  try {
    return Math.max(0, Number(localStorage.getItem(STREAK_KEY)) || 0);
  } catch {
    return 0;
  }
}

function saveStreak(n: number): void {
  try {
    localStorage.setItem(STREAK_KEY, String(n));
  } catch {
    /* webview may block storage */
  }
}

function fmtPawns(cp: number): string {
  return (Math.abs(cp) / 100).toFixed(1);
}

/** Разобрать `?g=<gameId>&p=<ply>` из хвоста хэша (дип-линк из разбора). */
function parseDeepLink(): { g: string; p: number } | null {
  const q = location.hash.split('?')[1];
  if (!q) return null;
  const params = new URLSearchParams(q);
  const g = params.get('g');
  const p = Number(params.get('p'));
  return g && Number.isFinite(p) ? { g: decodeURIComponent(g), p } : null;
}

/** Поднять пазл дип-линка в начало очереди. */
function orderPuzzles(puzzles: OwnPuzzle[]): OwnPuzzle[] {
  const link = parseDeepLink();
  if (!link) return puzzles;
  const idx = puzzles.findIndex((p) => p.gameId === link.g && p.ply === link.p);
  if (idx <= 0) return puzzles;
  return [puzzles[idx]!, ...puzzles.slice(0, idx), ...puzzles.slice(idx + 1)];
}

export function renderTrainBlunders(root: HTMLElement): void {
  if (!state.nick) {
    location.hash = '#/';
    return;
  }
  const own = orderPuzzles(ownPuzzlesFromGames(state.games, state.nick));
  trackTraining();

  root.className = 'app v2';
  root.innerHTML = shell();
  hideMainButton();
  const back = (): void => {
    location.hash = '#/train';
  };
  showBackButton(back);
  root.querySelector<HTMLElement>('[data-back]')?.addEventListener('click', back);

  const boardStage = root.querySelector<HTMLElement>('[data-flash]')!;
  const boardEl = root.querySelector<HTMLElement>('[data-board]')!;
  const captionEl = root.querySelector<HTMLElement>('[data-caption]')!;
  const feedbackEl = root.querySelector<HTMLElement>('[data-feedback]')!;
  const streakEl = root.querySelector<HTMLElement>('[data-streak]')!;
  const eyebrowEl = root.querySelector<HTMLElement>('[data-eyebrow]')!;
  const badgeEl = root.querySelector<HTMLElement>('[data-badge]')!;
  const progressEl = root.querySelector<HTMLElement>('[data-progress]')!;

  const sched = new PriorityScheduler(loaderAnalyze);
  let play: PlayEngineController | null = null;
  const board = new MiniBoard(boardEl, {
    onMove: (san, fen) => {
      if (play?.active) play.handleUserMove(san, fen);
      else void onUserMove(san);
    },
  });

  let streak = loadStreak();
  let ownIndex = 0;
  let puzzle: TrainPuzzle | null = null;
  let wrong = 0;
  let locked = false;
  let flipped = false; // ⇅ сохраняется между задачами в сессии
  let usedHint = false; // подсказку смотрели → серию не растим
  const nav = new AttemptNav('');

  play = new PlayEngineController({
    board,
    host: root.querySelector<HTMLElement>('[data-play-host]')!,
    analyze: (fen) => sched.requestPriority(fen, PLAY_OPTS),
    exitLabel: t('play.exit_train'),
    onExit: returnFromPlay,
    onHintBusy: (busy) => toolbar.setHintBusy(busy),
  });

  const host: BoardHost = {
    first: () => goFrame(nav.toStart()),
    prev: () => goFrame(nav.back()),
    next: () => goFrame(nav.forward()),
    last: () => {
      while (nav.canForward) nav.forward();
      goFrame(nav.current());
    },
    navState: () => ({ atStart: !nav.canBack, atEnd: !nav.canForward }),
    flip: onFlip,
    applyBest: paintTaskBest,
    startPlay: onPlayOut,
    play: play!,
  };
  const toolbar = new BoardToolbar(host);
  root.querySelector<HTMLElement>('[data-toolbar]')!.appendChild(toolbar.el);

  /** Ориентация с учётом флипа сессии. */
  function orient(): 'white' | 'black' {
    const base = puzzle ? puzzle.orientation : 'white';
    return flipped ? (base === 'white' ? 'black' : 'white') : base;
  }

  function refreshNav(): void {
    toolbar.refresh();
  }

  /** 💡 в режиме задачи: стрелка лучшего хода пазла (только на позиции задачи). */
  function paintTaskBest(show: boolean): void {
    if (show && puzzle && nav.onTask) {
      usedHint = true; // увидел ответ → серию не растим
      const m = puzzle.bestUci;
      board.setArrow({ from: m.slice(0, 2), to: m.slice(2, 4) });
    } else {
      board.setArrow(null);
    }
  }

  /** Показать кадр навигации; 💡-стрелку перерисовываем под кадр (persistent-тумблер). */
  function goFrame(f: Frame): void {
    board.setOrientation(orient());
    board.setPosition(f.fen, f.last);
    paintTaskBest(toolbar.bestOn);
    refreshNav();
  }

  function setStreak(): void {
    streakEl.textContent = t('train.streak', { n: streak });
  }

  function setFeedback(kind: '' | 'good' | 'bad' | 'warn', html: string): void {
    boardStage.classList.remove('flash-good', 'flash-bad', 'flash-warn');
    if (kind) boardStage.classList.add(`flash-${kind}`);
    feedbackEl.className = `card feedback-card ${kind}`.trim();
    feedbackEl.innerHTML = html;
    feedbackEl.classList.toggle('hidden', !html);
  }

  function renderPuzzle(p: TrainPuzzle): void {
    puzzle = p;
    wrong = 0;
    locked = false;
    usedHint = false;
    nav.reset(p.parentFen);
    boardStage.classList.remove('hidden');
    toolbar.el.classList.remove('hidden');
    goFrame(nav.current());
    setFeedback('', '');
    if (p.source === 'own') {
      const num = `${p.moveNo}${p.white ? '.' : '…'}`;
      eyebrowEl.textContent = t('train.progress', { n: ownIndex + 1, total: own.length });
      progressEl.textContent = t('train.progress', { n: ownIndex + 1, total: own.length });
      badgeEl.textContent = p.opponent ? t('train.from_game', { name: p.opponent }) : '';
      badgeEl.classList.toggle('hidden', !p.opponent);
      captionEl.innerHTML = `${t(p.white ? 'train.turn_white' : 'train.turn_black')} ${t('train.you_played', {
        move: `${num}${esc(p.playedSan)}??`,
      })}`;
    } else {
      eyebrowEl.textContent = '';
      progressEl.textContent = t('train.lichess_badge');
      badgeEl.textContent = p.rating ? t('train.rating', { n: p.rating }) : t('train.lichess_badge');
      badgeEl.classList.remove('hidden');
      captionEl.textContent = t(p.white ? 'train.turn_white' : 'train.turn_black');
    }
  }

  function correct(p: TrainPuzzle): void {
    notify(usedHint ? 'warning' : 'success');
    streak = streakAfterCorrect(streak, usedHint);
    saveStreak(streak);
    setStreak();
    const evalMover = p.bestCpWhite != null ? (p.white ? p.bestCpWhite : -p.bestCpWhite) : null;
    const body =
      evalMover != null
        ? t('train.correct_body', { move: esc(p.bestSan), eval: fmtEval(evalMover) })
        : t('train.correct_body_plain', { move: esc(p.bestSan) });
    const title = usedHint ? t('train.correct_hint') : t('train.correct');
    setFeedback('good', `<strong>${title}</strong><p>${body}</p>${cta('next', t('train.next'))}`);
    wireCta('next', advance);
  }

  function played(p: OwnPuzzle): void {
    notify('error');
    streak = nextStreak(streak, 'played');
    saveStreak(streak);
    setStreak();
    const body = p.lossCp > 0 ? t('train.played_body', { loss: fmtPawns(p.lossCp) }) : t('train.played_body_plain');
    setFeedback('bad', `<strong>${t('train.played')}</strong><p>${body}</p>${cta('retry', t('train.retry'))}`);
    wireCta('retry', () => resetAttempt(p));
  }

  function weak(p: TrainPuzzle): void {
    notify('warning');
    streak = nextStreak(streak, 'weak');
    saveStreak(streak);
    setStreak();
    wrong++;
    if (wrong >= 2) {
      const m = p.bestUci;
      board.setArrow({ from: m.slice(0, 2), to: m.slice(2, 4) });
    }
    const body = wrong >= 2 ? t('train.weak_body_hint') : t('train.weak_body');
    setFeedback('warn', `<strong>${t('train.weak')}</strong><p>${body}</p>${cta('retry', t('train.retry'))}`);
    wireCta('retry', () => resetAttempt(p));
  }

  function resetAttempt(p: TrainPuzzle): void {
    locked = false;
    nav.reset(p.parentFen);
    goFrame(nav.current());
    setFeedback('', '');
  }

  async function onUserMove(san: string): Promise<void> {
    if (!puzzle || locked) return;
    if (!nav.onTask) {
      goFrame(nav.current()); // листали попытку — доска не для решения
      return;
    }
    locked = true;
    const p = puzzle;
    const uci = sanToUci(p.parentFen, san);
    if (!uci) {
      locked = false;
      return;
    }
    const after = fenAfterUci(p.parentFen, uci);
    if (after) nav.push(after, { from: uci.slice(0, 2), to: uci.slice(2, 4) });
    refreshNav();
    haptic('medium');
    if (uci !== p.bestUci && !(p.source === 'own' && uci === p.playedUci)) {
      setFeedback('', `<p class="muted">${t('train.checking')}</p>`);
    }
    const verdict: Verdict = await verifyAttempt(p, uci, (fen) => sched.requestPriority(fen, ON_DEMAND));
    if (puzzle !== p) return; // ушли на другой пазл, пока думал движок
    if (verdict === 'best') correct(p);
    else if (verdict === 'played' && p.source === 'own') played(p);
    else weak(p);
  }

  // --- тулбар ------------------------------------------------------------------
  function onFlip(): void {
    if (!puzzle) return;
    flipped = !flipped;
    board.setOrientation(orient()); // работает и в игре: setPosition движка ориентацию не трогает
  }

  function onPlayOut(): void {
    if (play?.active || !puzzle) return;
    trackTraining('play_engine');
    setFeedback('', ''); // убрать вердикт задачи — при 🤖 не мешаем игре с ботом
    play?.start(nav.current().fen, puzzle.white ? 'w' : 'b');
    board.setOrientation(orient()); // уважить флип сессии (start ставит ориентацию по стороне юзера)
  }

  function returnFromPlay(): void {
    if (!puzzle) {
      exhausted();
      toolbar.onPlayExit();
      return;
    }
    locked = false;
    goFrame(nav.toStart());
    setFeedback('', '');
    toolbar.onPlayExit();
  }

  function advance(): void {
    if (puzzle?.source === 'own') archivePuzzle(puzzle); // пройдена → в архив навсегда
    if (puzzle?.source === 'own' && ownIndex + 1 < own.length) {
      ownIndex++;
      renderPuzzle(own[ownIndex]!);
    } else {
      exhausted();
    }
  }

  function exhausted(): void {
    puzzle = null;
    board.setArrow(null);
    setFeedback('', '');
    captionEl.textContent = '';
    eyebrowEl.textContent = '';
    badgeEl.classList.add('hidden');
    progressEl.textContent = '';
    boardStage.classList.add('hidden');
    toolbar.el.classList.add('hidden');
    feedbackEl.classList.remove('hidden');
    feedbackEl.className = 'card feedback-card';
    feedbackEl.innerHTML = `<strong>${t('train.exhausted')}</strong><p>${t('train.exhausted_sub')}</p>${cta(
      'lichess',
      t('train.lichess_cta'),
    )}`;
    wireCta('lichess', loadLichess);
  }

  async function loadLichess(): Promise<void> {
    feedbackEl.innerHTML = `<p class="muted">${t('train.loading_puzzle')}</p>`;
    const p = await fetchLichessPuzzle();
    if (p) {
      renderPuzzle(p);
    } else {
      feedbackEl.innerHTML = `<strong>${t('train.puzzle_failed')}</strong>${cta('lichess', t('train.retry'))}`;
      wireCta('lichess', loadLichess);
    }
  }

  function wireCta(id: string, fn: () => void): void {
    feedbackEl.querySelector<HTMLElement>(`[data-cta="${id}"]`)?.addEventListener('click', fn);
  }

  setStreak();
  if (own.length) {
    renderPuzzle(own[0]!);
  } else {
    exhausted();
  }
}

function fmtEval(cpMover: number): string {
  if (Math.abs(cpMover) >= 2000) return cpMover > 0 ? '+#' : '−#';
  const p = cpMover / 100;
  const sign = p > 0 ? '+' : p < 0 ? '−' : '';
  return `${sign}${Math.abs(p).toFixed(1)}`;
}

function cta(id: string, label: string): string {
  return `<button type="button" class="inline-cta" data-cta="${id}">${label}</button>`;
}

function shell(): string {
  return `
    <header class="topbar">
      <span class="back" data-back>${t('top.back')}</span>
      <span class="top-title"><span class="top-logo" aria-hidden="true"></span>${t('train.blunders_title')}</span>
      <span class="top-action" data-progress></span>
    </header>
    <section class="review-head train-head">
      <div class="row between gap12">
        <div class="grow">
          <div class="eyebrow" data-eyebrow></div>
          <h1 style="font-size:24px;margin-top:4px">${t('train.find_best')}</h1>
        </div>
        <span class="badge hidden" data-badge></span>
      </div>
    </section>
    <section class="train-board-card">
      <div class="train-board-stage" data-flash><div class="board-stage" data-board></div></div>
      <p class="tiny board-caption" data-caption></p>
    </section>
    <div data-toolbar></div>
    <section class="card feedback-card hidden" data-feedback></section>
    <div data-play-host></div>
    <p class="streak" data-streak></p>`;
}
